/**
 * Who a broadcast reaches, and how the console talks about it.
 *
 * The number beside the Send button is the only thing standing between an operator and mailing
 * the wrong population, so everything that shapes it lives here, tested, rather than inline next
 * to the button.
 *
 * Two things are worth stating plainly, because both are places a console can lie:
 *
 *   1. **Matched is not deliverable.** The contract returns both, and they differ by everyone
 *      with no email address on file and everyone who opted out of marketing. Quoting `matched`
 *      as "recipients" promises a reach the platform will not deliver, and the gap is not small:
 *      an opted-out share of a base is routinely double digits.
 *   2. **A customer with no address is not a recipient.** `adminUserRowSchema` makes `email`
 *      nullable, because a phone-only account predates the credential migration and has nothing
 *      to send to. A hand-picked set of ten customers can be a send to eight.
 */
import type { AdminUserRow, BroadcastAudience, BroadcastAudienceKind, BroadcastPreviewResponse, KycTier } from '../contracts/types.js';

/**
 * A deliberately plain address check, matching the one the rest of the console uses. It is not
 * trying to be RFC 5322; its whole job is to catch the typo an operator can see once it is
 * pointed at. The mail server is the authority on whether an address exists.
 */
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function isEmailAddress(value: string): boolean {
  return EMAIL_RE.test(value.trim());
}

/** How many customers one broadcast may name explicitly. Mirrors `BROADCAST_MAX_NAMED_USERS`. */
export const BROADCAST_MAX_NAMED_USERS = 500;

export interface AudienceKindInfo {
  readonly label: string;
  /** One line saying who is in it, in the operator's words rather than the schema's. */
  readonly hint: string;
}

export const AUDIENCE_KIND_INFO: Readonly<Record<BroadcastAudienceKind, AudienceKindInfo>> = {
  users: {
    label: 'Named customers',
    hint: 'One person or several, picked by hand. This is also how you write to a single customer.',
  },
  all: {
    label: 'Every customer',
    hint: 'Everyone with an active account.',
  },
  verified: {
    label: 'Verified email',
    hint: 'Everyone whose email address is confirmed. The only group that can reliably receive mail.',
  },
  unverified: {
    label: 'Unverified email',
    hint: 'Everyone who has not confirmed their address yet. Useful for a finish signing up nudge.',
  },
  tier: {
    label: 'One KYC tier',
    hint: 'Everyone at the tier you choose.',
  },
  signed_up_between: {
    label: 'Signed up in a date range',
    hint: 'Everyone who joined between two dates.',
  },
};

export interface AudienceDraft {
  readonly kind: BroadcastAudienceKind;
  readonly userIds: readonly string[];
  readonly tier: KycTier;
  readonly from: string;
  readonly to: string;
}

export function emptyAudienceDraft(): AudienceDraft {
  return { kind: 'verified', userIds: [], tier: 'tier_1', from: '', to: '' };
}

export type AudienceResult =
  | { readonly audience: BroadcastAudience }
  | { readonly error: string };

/**
 * Turn the form's state into the contract's discriminated union, or say why it cannot be.
 *
 * Every refusal is a sentence an operator can act on. The date-range rule is the contract's own
 * cross-field check, applied here so the operator hears it from the field rather than from a
 * validation error after composing a whole message.
 */
export function buildAudience(draft: AudienceDraft): AudienceResult {
  switch (draft.kind) {
    case 'users': {
      if (draft.userIds.length === 0) return { error: 'Pick at least one customer.' };
      if (draft.userIds.length > BROADCAST_MAX_NAMED_USERS) {
        return {
          error: `Name at most ${String(BROADCAST_MAX_NAMED_USERS)} customers by hand, or choose a group instead.`,
        };
      }
      return { audience: { kind: 'users', userIds: [...draft.userIds] } };
    }
    case 'tier':
      return { audience: { kind: 'tier', tier: draft.tier } };
    case 'signed_up_between': {
      if (draft.from === '' || draft.to === '') return { error: 'Choose both a start and an end date.' };
      const from = Date.parse(draft.from);
      const to = Date.parse(draft.to);
      if (Number.isNaN(from) || Number.isNaN(to)) return { error: 'Those are not both dates.' };
      if (from > to) return { error: 'The start of the range must not be after the end.' };
      return {
        audience: {
          kind: 'signed_up_between',
          from: new Date(from).toISOString(),
          to: new Date(to).toISOString(),
        },
      };
    }
    case 'all':
    case 'verified':
    case 'unverified':
      return { audience: { kind: draft.kind } };
  }
}

/** The picked customers, split by whether there is anywhere to send to. */
export function splitAddressable(users: readonly AdminUserRow[]): {
  readonly addressable: readonly AdminUserRow[];
  readonly unaddressable: readonly AdminUserRow[];
} {
  const addressable: AdminUserRow[] = [];
  const unaddressable: AdminUserRow[] = [];
  for (const user of users) {
    if (user.email !== null && isEmailAddress(user.email)) addressable.push(user);
    else unaddressable.push(user);
  }
  return { addressable, unaddressable };
}

export function countAddressable(users: readonly AdminUserRow[]): number {
  return splitAddressable(users).addressable.length;
}

/**
 * The sentence the composer prints under the count.
 *
 * Deliberately says what was skipped and why. "4,820 matched, 3,900 will be sent" is a fact an
 * operator can act on; "4,820 recipients" when 920 of them are opted out is a promise the
 * platform will not keep, and the person who finds out is the one asked why the campaign
 * underperformed.
 */
export function describePreview(preview: BroadcastPreviewResponse | null): {
  readonly deliverable: number | null;
  readonly detail: string | null;
} {
  if (preview === null) return { deliverable: null, detail: null };
  const skipped = preview.skipped.noEmail + preview.skipped.optedOut;
  if (skipped === 0) {
    return { deliverable: preview.deliverable, detail: null };
  }
  const parts: string[] = [];
  if (preview.skipped.noEmail > 0) {
    parts.push(`${preview.skipped.noEmail.toLocaleString()} with no email address`);
  }
  if (preview.skipped.optedOut > 0) {
    parts.push(`${preview.skipped.optedOut.toLocaleString()} who opted out of marketing`);
  }
  return {
    deliverable: preview.deliverable,
    detail: `${preview.matched.toLocaleString()} customers match. ${parts.join(' and ')} will be skipped.`,
  };
}

/**
 * Whether the typed confirmation matches.
 *
 * Trimmed, because a trailing space from a paste is not a different intention, and
 * case-insensitive, because the point of the gesture is deliberateness rather than a
 * capitalisation quiz. It is still a word the operator had to type on purpose.
 */
export function confirmationMatches(typed: string, phrase: string): boolean {
  return typed.trim().toLowerCase() === phrase.trim().toLowerCase();
}

/** A short, safe label for one customer. Never an unmasked phone number. */
export function recipientLabel(user: AdminUserRow): string {
  const name = user.displayName?.trim();
  if (name !== undefined && name !== '') return name;
  if (user.username !== null && user.username !== '') return user.username;
  return user.email ?? user.maskedPhone;
}
